import { MOCK_CLIENTS, MOCK_KEYWORDS, MOCK_RANKINGS, MOCK_HISTORY, MOCK_DISCOVERED } from './mockData';

// Environment variables - no hardcoded URLs
const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.VITE_API_URL || '';
const USE_MOCK = process.env.REACT_APP_USE_MOCK === 'true' || process.env.VITE_USE_MOCK === 'true';
const ADMIN_TOKEN = process.env.REACT_APP_ADMIN_PASSWORD || process.env.VITE_ADMIN_PASSWORD || '';

// Debug logging
console.log('[API Config]', { API_BASE_URL, USE_MOCK, hasAdminToken: !!ADMIN_TOKEN });

const delay = (ms = 600) => new Promise(r => setTimeout(r, ms));

/**
 * Generic API request handler
 * @param {string} method - HTTP method
 * @param {string} path - API endpoint path (without base URL)
 * @param {object} body - Request body (optional)
 * @param {boolean} isAdmin - Whether to include admin auth header
 * @returns {Promise<{data: any, error: string|null}>}
 */
async function request(method, path, body = null, isAdmin = false) {
  if (USE_MOCK) return { data: null, error: null };
  
  try {
    const headers = { 'Content-Type': 'application/json' };
    if (isAdmin && ADMIN_TOKEN) {
      headers['Authorization'] = `Bearer ${ADMIN_TOKEN}`;
    }

    const url = `${API_BASE_URL}${path}`;
    console.log(`[API] ${method} ${url}`, body ? { body } : '');

    const res = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    // Handle non-JSON responses
    const contentType = res.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      if (!res.ok) {
        return { data: null, error: `Request failed with status ${res.status}` };
      }
      return { data: null, error: null };
    }

    const json = await res.json();
    
    if (!res.ok) {
      console.error(`[API Error] ${method} ${path}:`, json);
      return { data: null, error: json.error || json.message || `Request failed with status ${res.status}` };
    }
    
    console.log(`[API Response] ${method} ${path}:`, json);
    return { data: json.data ?? json, error: null };
  } catch (err) {
    console.error(`[API Exception] ${method} ${path}:`, err);
    return { data: null, error: err.message || 'Network error' };
  }
}

// ============================================
// HEALTH CHECK
// ============================================
export async function healthCheck() {
  if (USE_MOCK) return { data: { status: 'ok' }, error: null };
  return request('GET', '/health');
}

// ============================================
// CLIENTS
// ============================================
export async function getClients() {
  if (USE_MOCK) { await delay(); return { data: MOCK_CLIENTS, error: null }; }
  return request('GET', '/clients');
}

export async function getClient(id) {
  if (USE_MOCK) { await delay(); return { data: MOCK_CLIENTS.find(c => c.id === id), error: null }; }
  return request('GET', `/clients/${id}`);
}

export async function createClient(payload) {
  if (USE_MOCK) {
    await delay();
    const c = { id: Date.now(), ...payload, active: 1, created_at: new Date().toISOString() };
    MOCK_CLIENTS.push(c);
    return { data: c, error: null };
  }
  return request('POST', '/clients', payload, true);
}

export async function updateClient(id, payload) {
  if (USE_MOCK) {
    await delay();
    const idx = MOCK_CLIENTS.findIndex(c => c.id === id);
    if (idx >= 0) MOCK_CLIENTS[idx] = { ...MOCK_CLIENTS[idx], ...payload };
    return { data: MOCK_CLIENTS[idx], error: null };
  }
  return request('PUT', `/clients/${id}`, payload, true);
}

export async function deleteClient(id) {
  if (USE_MOCK) { 
    await delay(); 
    const i = MOCK_CLIENTS.findIndex(c => c.id === id); 
    if (i >= 0) MOCK_CLIENTS.splice(i, 1); 
    return { data: { success: true }, error: null }; 
  }
  return request('DELETE', `/clients/${id}`, null, true);
}

export async function uploadClientLogo(id, file) {
  if (USE_MOCK) { await delay(); return { data: { success: true }, error: null }; }
  
  try {
    const formData = new FormData();
    formData.append('logo', file);
    
    const res = await fetch(`${API_BASE_URL}/clients/${id}/logo`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${ADMIN_TOKEN}`,
      },
      body: formData,
    });
    
    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      return { data: null, error: json.error || 'Upload failed' };
    }
    
    return { data: await res.json(), error: null };
  } catch (err) {
    return { data: null, error: err.message || 'Upload error' };
  }
}

// ============================================
// KEYWORDS
// ============================================
export async function getKeywords(clientId) {
  if (USE_MOCK) { 
    await delay(); 
    return { data: MOCK_KEYWORDS.filter(k => k.client_id === clientId && k.is_target === 1), error: null }; 
  }
  const response = await request('GET', `/clients/${clientId}/keywords`);
  // API returns all keywords; filter to only tracked (is_target=1) for display
  if (response.data && Array.isArray(response.data)) {
    response.data = response.data.filter(k => k.is_target === 1);
  }
  return response;
}

export async function addKeywords(clientId, keywords) {
  if (USE_MOCK) {
    await delay();
    const added = keywords.map((kw, i) => ({ 
      id: Date.now() + i, 
      client_id: clientId, 
      ...kw, 
      is_target: 1, 
      created_at: new Date().toISOString() 
    }));
    MOCK_KEYWORDS.push(...added);
    return { data: added, error: null };
  }
  // API accepts one keyword at a time — send individual requests
  const results = [];
  const errors = [];
  for (const kw of keywords) {
    const response = await request('POST', `/clients/${clientId}/keywords`, kw, true);
    if (response.error) {
      errors.push(`${kw.keyword}: ${response.error}`);
    } else if (response.data) {
      results.push(response.data);
    }
  }
  if (errors.length > 0 && results.length === 0) {
    return { data: null, error: errors.join('; ') };
  }
  return { data: results, error: errors.length > 0 ? `${errors.length} failed` : null };
}

export async function deleteKeyword(clientId, kwId) {
  if (USE_MOCK) { 
    await delay(); 
    const i = MOCK_KEYWORDS.findIndex(k => k.id === kwId); 
    if (i >= 0) MOCK_KEYWORDS.splice(i, 1); 
    return { data: { success: true }, error: null }; 
  }
  return request('DELETE', `/clients/${clientId}/keywords/${kwId}`, null, true);
}

export async function updateKeyword(clientId, kwId, payload) {
  if (USE_MOCK) { await delay(); return { data: payload, error: null }; }
  return request('PATCH', `/clients/${clientId}/keywords/${kwId}`, payload, true);
}

export async function importKeywordsCSV(clientId, file) {
  if (USE_MOCK) { await delay(); return { data: { imported: 0 }, error: null }; }
  
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const res = await fetch(`${API_BASE_URL}/clients/${clientId}/keywords/import`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${ADMIN_TOKEN}`,
      },
      body: formData,
    });
    
    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      return { data: null, error: json.error || 'Import failed' };
    }
    
    return { data: await res.json(), error: null };
  } catch (err) {
    return { data: null, error: err.message || 'Import error' };
  }
}

// ============================================
// RANKINGS
// ============================================
export async function getRankings(clientId) {
  if (USE_MOCK) { 
    await delay(); 
    return { 
      data: MOCK_RANKINGS.filter(r => { 
        const kw = MOCK_KEYWORDS.find(k => k.id === r.keyword_id); 
        return kw && kw.client_id === clientId; 
      }), 
      error: null 
    }; 
  }
  
  const response = await request('GET', `/clients/${clientId}/rankings?limit=500`);
  
  // Map backend response to frontend expected format
  if (response.data && response.data.rankings) {
    const mappedRankings = response.data.rankings.map(r => ({
      keyword_id: r.keyword_id,
      keyword: r.keyword,
      location: r.location,
      rank: r.current_rank, // Map current_rank to rank
      url: r.url,
      snippet: r.snippet,
      competitors: r.competitors ? r.competitors.map(c => c.domain) : [],
      scanned_at: r.scanned_at,
      previous_rank: r.previous_rank,
      rank_delta: r.rank_delta,
      serpFeatures: r.serpFeatures,
      localPackRank: r.localPackRank,
    }));
    return { data: mappedRankings, error: null };
  }
  
  return response;
}

export async function getRankingHistory(clientId) {
  if (USE_MOCK) { await delay(); return { data: MOCK_HISTORY[clientId] || [], error: null }; }
  const response = await request('GET', `/clients/${clientId}/rankings/history?limit=500`);
  // API returns { history: [...], total, limit } — unwrap to array
  if (response.data && response.data.history) {
    response.data = response.data.history;
  }
  return response;
}

export async function getRankingDistribution(clientId) {
  if (USE_MOCK) { 
    await delay(); 
    return { data: { top3: 3, top10: 7, top20: 2, unranked: 0 }, error: null }; 
  }
  const response = await request('GET', `/clients/${clientId}/rankings/distribution`);
  // API returns { top_3, top_10, top_20, not_ranked, total } — map to frontend keys
  if (response.data) {
    const d = response.data;
    response.data = {
      top3: d.top_3 ?? 0,
      top10: d.top_10 ?? 0,
      top20: d.top_20 ?? 0,
      unranked: d.not_ranked ?? 0,
      total: d.total ?? 0,
    };
  }
  return response;
}

export async function getRankingCompetitors(clientId) {
  if (USE_MOCK) { 
    await delay(); 
    return { data: [], error: null }; 
  }
  const response = await request('GET', `/clients/${clientId}/rankings/competitors`);
  // API returns { competitors: [...] } — unwrap
  if (response.data && response.data.competitors) {
    response.data = response.data.competitors;
  }
  return response;
}

export async function getRankingCannibalization(clientId) {
  if (USE_MOCK) { await delay(); return { data: [], error: null }; }
  const response = await request('GET', `/clients/${clientId}/rankings/cannibalization`);
  // API returns { cannibalization: [...] } — unwrap
  if (response.data && response.data.cannibalization) {
    response.data = response.data.cannibalization;
  }
  return response;
}

// CSV export — triggers browser download via blob URL
export async function exportRankingsCSV(clientId, clientName) {
  if (USE_MOCK) { 
    // Generate CSV from mock data
    const rankings = MOCK_RANKINGS.filter(r => { 
      const kw = MOCK_KEYWORDS.find(k => k.id === r.keyword_id); 
      return kw && kw.client_id === clientId; 
    });
    const csvContent = [
      ['Keyword', 'Location', 'Rank', 'Snippet', 'Scanned At'].join(','),
      ...rankings.map(r => [
        `"${r.keyword}"`,
        `"${r.location}"`,
        r.rank || 'Not Ranked',
        `"${r.snippet || ''}"`,
        r.scanned_at
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(clientName || 'client').replace(/[^a-z0-9]/gi, '_')}_rankings.csv`;
    a.click();
    URL.revokeObjectURL(url);
    return;
  }
  
  try {
    const res = await fetch(`${API_BASE_URL}/clients/${clientId}/rankings/export`, {
      headers: { 'Authorization': `Bearer ${ADMIN_TOKEN}` }
    });
    
    if (!res.ok) {
      console.error('[API Error] CSV export failed:', res.status);
      return;
    }
    
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(clientName || 'client').replace(/[^a-z0-9]/gi, '_')}_rankings.csv`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error('[API Exception] CSV export failed:', err);
  }
}

// ============================================
// SCAN
// ============================================
let mockScanDone = 0;
let mockScanTotal = 0;

export async function triggerScan(clientId) {
  if (USE_MOCK) {
    await delay(300);
    const kws = MOCK_KEYWORDS.filter(k => k.client_id === clientId && k.is_target === 1);
    mockScanDone = 0;
    mockScanTotal = kws.length || 12;
    return { data: { jobId: 'mock_job_001', totalQueries: mockScanTotal }, error: null };
  }
  const response = await request('POST', '/scan', { clientId }, true);
  // Normalize scan response — API may use job_id or id instead of jobId
  if (response.data) {
    const d = response.data;
    response.data = {
      jobId: d.jobId || d.job_id || d.id,
      totalQueries: d.totalQueries || d.total_queries || d.total || 0,
    };
  }
  return response;
}

export async function getScanStatus(jobId) {
  if (USE_MOCK) {
    await delay(400);
    mockScanDone = Math.min(mockScanDone + 1, mockScanTotal);
    const percent = Math.round((mockScanDone / mockScanTotal) * 100);
    const status = mockScanDone >= mockScanTotal ? 'completed' : 'running';
    return { data: { jobId, done: mockScanDone, total: mockScanTotal, percent, status }, error: null };
  }
  const response = await request('GET', `/scan/status/${jobId}`);
  // Normalize scan status response
  if (response.data) {
    const d = response.data;
    response.data = {
      jobId: d.jobId || d.job_id || d.id || jobId,
      done: d.done ?? 0,
      total: d.total ?? 0,
      percent: d.percent ?? (d.total ? Math.round((d.done / d.total) * 100) : 0),
      status: d.status || 'running',
    };
  }
  return response;
}

// ============================================
// DISCOVERY
// ============================================
export async function runDiscovery(clientId) {
  if (USE_MOCK) { await delay(800); return { data: { found: 5 }, error: null }; }
  return request('POST', `/clients/${clientId}/discover`, null, true);
}

export async function getDiscovered(clientId) {
  if (USE_MOCK) { await delay(); return { data: MOCK_DISCOVERED[clientId] || [], error: null }; }
  const response = await request('GET', `/clients/${clientId}/discovered`);
  // API returns { keywords: [...], count } — unwrap to array
  if (response.data && response.data.keywords) {
    response.data = response.data.keywords;
  }
  return response;
}

export async function promoteKeyword(clientId, kwId) {
  if (USE_MOCK) {
    await delay();
    const kw = (MOCK_DISCOVERED[clientId] || []).find(k => k.id === kwId);
    if (kw) {
      kw.is_target = 1;
      MOCK_KEYWORDS.push({ ...kw, is_target: 1 });
      // Remove from discovered
      const idx = MOCK_DISCOVERED[clientId].findIndex(k => k.id === kwId);
      if (idx >= 0) MOCK_DISCOVERED[clientId].splice(idx, 1);
    }
    return { data: kw, error: null };
  }
  return request('POST', `/clients/${clientId}/discovered/${kwId}/promote`, null, true);
}

// ============================================
// ADMIN
// ============================================
export async function verifyAdmin() {
  if (USE_MOCK) { await delay(200); return { data: { success: true }, error: null }; }
  const response = await request('POST', '/admin/verify', {}, true);
  // API returns { valid: true } — map to { success: true }
  if (response.data && response.data.valid !== undefined) {
    response.data = { success: response.data.valid };
  }
  return response;
}

export async function getSerpApiUsage() {
  const response = await request('GET', '/admin/serpapi-usage');
  return response;
}

export async function getProviders() {
  return await request('GET', '/admin/providers');
}

export async function switchProvider(provider) {
  return await request('POST', '/admin/providers/switch', { provider });
}

export async function getProvidersUsage() {
  return await request('GET', '/admin/providers/usage');
}

export async function getAdminStats() {
  if (USE_MOCK) { 
    await delay(); 
    return { data: { clients: 3, keywords: 36, scanResults: 144, recentJobs: [] }, error: null }; 
  }
  const response = await request('GET', '/admin/stats', null, true);
  // API returns { scans } — map to { scanResults }
  if (response.data && response.data.scans !== undefined) {
    response.data.scanResults = response.data.scans;
    delete response.data.scans;
  }
  return response;
}
